import React, { useCallback, useMemo } from 'react';

import {
  Form,
  useCreate,
  useGetOne,
  useListContext,
  useNotify,
  usePermissions,
} from 'react-admin';

import {
  Button,
  Dialog,
  DialogTitle,
} from '@mui/material';

import CloudQueueIcon from '@mui/icons-material/CloudQueue';

import useDialogStatus from '../../../../hooks/useDialogStatus';
import actions from '../../../../constants/actions';
import { getMatchingPermissions } from '../../../../utilities/hasAccess';
import { Permission } from '../../../../utilities/Authprovider';
import usePendingReview from '../../../../hooks/usePendingReview';
import errorNotification from '../../../../utilities/errorNotification';
import ActionFormContent from './ActionFormContent';

const Release = ({
  disabled,
  version,
  onReleased,
}: {
  disabled: boolean;
  version: string;
  onReleased: () => void;
}) => {
  const { refetch } = useListContext();
  const notify = useNotify();
  const { permissions } = usePermissions<Permission[]>();

  const { data: ruleVersion, isLoading } = useGetOne('rule-version', { id: version }, { enabled: !disabled });

  const requirePeerReview = useMemo(() => {
    const matchingPermissions = getMatchingPermissions(permissions, actions.RULE_VERSION_RELEASE);
    const firstMatch = matchingPermissions[0];
    if (!firstMatch || typeof firstMatch.attributes !== 'object' || firstMatch.attributes === null) {
      return false;
    }
    return (firstMatch.attributes as { requirePeerReview?: boolean }).requirePeerReview === true;
  }, [permissions]);

  const { open, openDialog, closeDialog } = useDialogStatus();
  const {
    pendingReview,
    isLoading: isPendingReviewLoading,
    refetch: refetchPendingReview,
  } = usePendingReview(
    actions.RULE_VERSION_RELEASE,
    { ruleVersion: version },
    { enabled: open && requirePeerReview },
  );
  const [release, { isLoading: isReleasing }] = useCreate();

  const handleRelease = useCallback(({
    description,
    reasoning,
  }: { description?: string; reasoning?: string }) => {
    if (isReleasing) return;

    if (requirePeerReview) {
      release(
        'reviews',
        {
          data: {
            action: actions.RULE_VERSION_RELEASE,
            reasoning,
            actionInformation: { ruleVersion: version, description },
          },
        },
        {
          onSuccess: () => {
            notify('Review submitted successfully', { type: 'success' });
            closeDialog();
            refetch();
            refetchPendingReview();
          },
          onError: (error) => {
            notify(errorNotification(error, 'Failed to submit review'), { type: 'error' });
            refetchPendingReview();
          },
        },
      );
      return;
    }

    release(
      'rules/release',
      { data: { description, ruleVersion: version } },
      {
        onSuccess: () => {
          notify('Rule tree was released', { type: 'success' });
          closeDialog();
          refetch();
          onReleased();
        },
        onError: (error) => {
          notify(errorNotification(error, 'Rule tree could not be released'), { type: 'error' });
        },
      },
    );
  }, [
    release,
    version,
    notify,
    closeDialog,
    refetch,
    refetchPendingReview,
    onReleased,
    isReleasing,
    requirePeerReview,
  ]);

  if (isLoading) return null;

  if (!ruleVersion?.isTestVersion) return null;

  if (ruleVersion?.isActive) return null;

  return (
    <>
      <Button
        startIcon={<CloudQueueIcon />}
        onClick={openDialog}
        disabled={disabled}
      >
        Release
      </Button>
      <Dialog onClose={closeDialog} open={open} fullWidth maxWidth="sm">
        <Form
          onSubmit={handleRelease}
          record={{ description: '', reasoning: '' }}
        >
          <DialogTitle>Go live with test rule tree</DialogTitle>
          <ActionFormContent
            requirePeerReview={requirePeerReview}
            isSubmitting={isReleasing}
            pendingReview={open ? pendingReview : null}
            pendingReviewLoading={open ? isPendingReviewLoading : false}
            closeDialog={closeDialog}
          />
        </Form>
      </Dialog>
    </>
  );
};

export default Release;
